import { NutritionTable } from "@/app/nutrition-wizard/nutrition-table";
import { createDishInitialValue } from "@/app/nutrition/create-dish.schema";
import { EditorMode } from "@/app/nutrition/editor-mode.enum";
import { Meal } from "@/app/nutrition/meal.enum";
import {
  useCreateDishNutritionMutation,
  useDishesQuery,
  useLazyDishQuery,
  useLazyEquivalencesQuery,
  useLazyNutritionalInformationsQuery,
  useProductNutritionsQuery,
} from "@/app/nutrition/nutrition.api";
import { Option } from "@/app/nutrition/option.enum";
import { nutritionSearchInitialValues } from "@/app/nutrition/search.schema";
import { Button } from "@/shared/components/atoms/Button";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import {
  NutritionMealTable,
  NutritionMealTableHandler,
} from "@/shared/components/organisms/NutritionMealTable";
import { SearchDishes } from "@/shared/components/organisms/SearchDishes";
import { TabsUser } from "@/shared/components/organisms/TabsUser";
import { useDebounce } from "@/shared/hooks/debounce";
import { toasts } from "@/shared/utils/toasts";
import { useFormik } from "formik";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useTranslation } from "react-i18next";

export type DishesPageProps = {};

export const DishesPage: FC<DishesPageProps> = () => {
  const [searchVisible, setSearchVisible] = useState(false);
  const { t } = useTranslation();
  const [editorMode, setEditorMode] = useState<EditorMode>(
    EditorMode.TrainingDay
  );
  const [equivalencesCache, setEquivalencesCache] = useState<
    Record<string, any>
  >({});
  const [nutritionalInformationsCache, setNutritionalInformationsCache] =
    useState<Record<string, any>>({});

  const [nutritionalInformationsQuery] = useLazyNutritionalInformationsQuery();
  const [equivalencesQuery] = useLazyEquivalencesQuery();
  const productNutritionQuery = useProductNutritionsQuery({
    nutritionTable: NutritionTable.AESTETHIC,
  });

  const [createDishNutrition, createDishNutritionResult] =
    useCreateDishNutritionMutation();

  const [dishQuery] = useLazyDishQuery();

  const searchFormik = useFormik({
    initialValues: nutritionSearchInitialValues,
    onSubmit: () => {},
  });

  const debouncedSearch = useDebounce(searchFormik.values, 300);

  const dishesQuery = useDishesQuery({
    search: debouncedSearch.dishes,
    page: debouncedSearch.page,
    meal: debouncedSearch.meal,
  });

  const formik = useFormik({
    initialValues: createDishInitialValue,
    onSubmit: (values) => {
      createDishNutrition({
        dish: values.nutrition?.breakfast.first,
        image: values.nutrition?.breakfast.first?.gallery?.file,
      });
    },
  });

  const nutritionMealTableKeyRef = useRef<NutritionMealTableHandler>();
  const [searchTab, setSearchTab] = useState(1);
  const searchTabs = useMemo(() => {
    return [
      { id: 1, name: "Crear a partir de un plato" },
      { id: 2, name: "Subir imagen" },
    ];
  }, []);

  const handleSetDish = async (meal, dish) => {
    const products = await dishQuery(dish).unwrap();

    dish.products.map((product) => {
      equivalencesQuery({ product })
        .unwrap()
        .then((response) => {
          setEquivalencesCache((equivalencesCache) => ({
            ...equivalencesCache,
            [product]: response,
          }));
        });
    });

    dish.products.map((product) => {
      nutritionalInformationsQuery({ product })
        .unwrap()
        .then((response) => {
          setNutritionalInformationsCache((nutritionalInformationsCache) => ({
            ...nutritionalInformationsCache,
            [product]: response,
          }));
        });
    });

    await formik.setFieldValue(`nutrition.${meal}.first`, {
      gallery: {
        id: dish.id,
        image: dish.image,
      },
      products: products.map((product) => ({
        product: product.product,
        portion: product.portion,
        portionAlt: product.portionAlt,
        portionRest: product.portionRest,
        portionRestAlt: product.portionRestAlt,
      })),
    });

    setTimeout(() => {
      nutritionMealTableKeyRef.current?.increaseKey();
    }, 1000);
  };

  const handleSetProduct = useCallback(
    async (product: any, meal: Meal, option: Option, index: number) => {
      nutritionalInformationsQuery({ product: product.name })
        .unwrap()
        .then((response) => {
          setNutritionalInformationsCache((nutritionalInformationsCache) => ({
            ...nutritionalInformationsCache,
            [product.name]: response,
          }));
        });

      equivalencesQuery({ product: product.name })
        .unwrap()
        .then((response) => {
          setEquivalencesCache((equivalencesCache) => ({
            ...equivalencesCache,
            [product.name]: response,
          }));
        });

      formik.setFieldValue(
        `nutrition.[${meal}].[${option}].products.[${index}]`,
        {
          product: product.name,
          portion: "0 gr",
          portionAlt: undefined,
          portionRest: "0 gr",
          portionRestAlt: undefined,
        }
      );
    },
    []
  );

  useEffect(() => {
    if (createDishNutritionResult.isSuccess) {
      toasts.success(
        t("contexts.nutrition.create_dish_success"),
        t("contexts.nutrition.create_dish_success_description")
      );
      formik.resetForm();
      setSearchVisible(false);
      nutritionMealTableKeyRef.current?.increaseKey();
    }
  }, [createDishNutritionResult]);

  return (
    <Fragment>
      <div className="flex flex-col flex-1 h-full w-full pt-8">
        <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
          <div className="flex-1 flex-col px-4 sm:px-6 lg:px-6">
            <div className="sm:flex sm:items-center">
              <div className="flex-grow">
                <h1 className="text-base font-semibold leading-6 text-gray-900">
                  {t("contexts.nutrition.dishes")}
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  {t("contexts.nutrition.manage_dishes")}
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0"></div>
            </div>
            <div className="border-b border-gray-300 mt-5"></div>
            <NutritionMealTable
              editWhenEmpty={
                formik.values.nutrition?.breakfast?.first?.gallery?.file
              }
              hideTabs
              onEmptyClick={() => setSearchVisible(!searchVisible)}
              onSetProduct={handleSetProduct}
              onOptionRight={() => {}}
              onOptionLeft={() => {}}
              onOptionDelete={() => {}}
              onOptionSwap={() => {}}
              editorMode={editorMode}
              equivalences={equivalencesCache}
              nutritionalInformations={nutritionalInformationsCache}
              meal={Meal.Breakfast}
              suplementations={[]}
              options={formik.values.nutrition[Meal.Breakfast]}
              macros={formik.values.macros[Meal.Breakfast]}
              onCurrentOptionChange={() => {}}
              onChange={formik.handleChange}
              setFieldValue={formik.setFieldValue}
              products={
                productNutritionQuery?.data?.map((product) => ({
                  ...product,
                  name: product.product,
                })) ?? []
              }
              ref={nutritionMealTableKeyRef}
            />
            <Button
              isLoading={createDishNutritionResult.isLoading}
              className="mt-4"
              text="Guardar"
              primary
              onClick={formik.handleSubmit}
            ></Button>
          </div>
        </div>
      </div>
      <DialogCreate
        isLoading={false}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate="Crear"
        open={searchVisible}
        setOpen={() => setSearchVisible(!searchVisible)}
        tittle={t("contexts.nutrition.create_dish")}
        subTittle={t("contexts.nutrition.create_dish_description")}
        onClose={() => setSearchVisible(false)}
        OnclickCancel={() => setSearchVisible(false)}
        hideFooter
      >
        <div className="flex flex-1 flex-col h-100">
          <div className="col-span-full px-4">
            <TabsUser
              tabs={searchTabs}
              selectedTab={searchTab}
              handleTabChange={(tab) => setSearchTab(tab)}
            />
          </div>
          {searchTab === 1 ? (
            <SearchDishes
              onlySelect
              onSetDish={handleSetDish}
              currentPage={searchFormik.values.page}
              lastPage={dishesQuery.data?.lastPage}
              perPage={dishesQuery.data?.perPage}
              total={dishesQuery.data?.total}
              dishes={dishesQuery?.data?.data}
              onChange={searchFormik.handleChange}
              onPageChange={(page) => {
                searchFormik.setFieldValue("page", page);
              }}
              setFieldValue={searchFormik.setFieldValue}
              values={searchFormik.values}
              isLoading={dishesQuery.isLoading || dishesQuery.isFetching}
            />
          ) : (
            <div className="p-4">
              <InputFile
                name="nutrition.breakfast.first.gallery.file"
                label={t("user.views.avatar")}
                spanButton={t("user.views.avatar")}
                description={t("user.views.avatar")}
                selectedImage={
                  formik.values.nutrition?.breakfast?.first?.gallery?.file
                }
                onFileChange={(file) => {
                  formik.setFieldValue(
                    "nutrition.breakfast.first.gallery.file",
                    file
                  );

                  const reader = new FileReader();
                  reader.onloadend = () => {
                    formik.setFieldValue(
                      "nutrition.breakfast.first.gallery.url",
                      reader.result
                    );
                  };
                  reader.readAsDataURL(file);

                  formik.setFieldValue(
                    "nutrition.breakfast.first.products",
                    []
                  );
                }}
                onRemoveImage={() =>
                  formik.setFieldValue(
                    "nutrition.breakfast.first.gallery.file",
                    null
                  )
                }
                tittleDelete={t("general.delete_image")}
              />
            </div>
          )}
        </div>
      </DialogCreate>
    </Fragment>
  );
};
